/**
 * useNodeExecutionSync Hook
 * Syncs execution state from designerModeStore to React Flow nodes
 *
 * This hook subscribes to nodeExecutionStates Map in the store and updates
 * the visual React Flow nodes with execution status (running, success, failed, etc.)
 */

import React from 'react';
import { Node } from 'reactflow';
import { useDesignerModeStore } from '@flow-studio/store'; // import { designerModeStore';
import type { NodeExecutionSyncStatus } from './types';

export const useNodeExecutionSync = (
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>
): NodeExecutionSyncStatus => {
  const nodeExecutionStates = useDesignerModeStore((s: any) => s.nodeExecutionStates);
  const mode = useDesignerModeStore((s: any) => s.mode);

  React.useEffect(() => {
    console.log(`[useNodeExecutionSync] Effect triggered - mode: ${mode}, mapSize: ${nodeExecutionStates.size}`);

    // Sync nodes based on mode
    if (mode === 'design') {
      console.log(`[useNodeExecutionSync] Clearing nodes (design mode)`);
      // Remove execution mode and execution status in design mode
      setNodes((currentNodes) => {
        return currentNodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            isInExecutionMode: false,
            runtime: {
              ...node.data.runtime,
              executionStatus: null, // Explicitly clear execution status in design mode
            }
          }
        }));
      });
      return;
    }

    // In execution/evaluation modes, update nodes with execution status and mode flag
    setNodes((currentNodes) => {
      console.log(`[useNodeExecutionSync] Updating ${currentNodes.length} nodes for execution mode`);
      const isMapEmpty = nodeExecutionStates.size === 0;

      const updatedNodes = currentNodes.map(node => {
        const execState = nodeExecutionStates.get(node.id);
        const newStatus = execState?.status || null;
        const oldStatus = node.data.runtime?.executionStatus;
        const statusChanged = oldStatus !== newStatus;
        const modeChanged = (node.data as any).isInExecutionMode !== true;

        // Always update when Map is empty (new execution starting) to clear old statuses
        if (!statusChanged && !modeChanged && !isMapEmpty) {
          return node;
        }

        if (statusChanged || isMapEmpty) {
          console.log(`[useNodeExecutionSync] Node ${node.id}: ${oldStatus} → ${newStatus}`);
        }

        return {
          ...node,
          data: {
            ...node.data,
            isInExecutionMode: true, // Flag that we're in execution/evaluation mode
            runtime: {
              ...node.data.runtime,
              executionStatus: newStatus, // 'running' | 'success' | 'failed' | 'skipped' | 'waiting' | 'retrying' | 'timeout'
            },
          },
        };
      });

      return updatedNodes;
    });
  }, [nodeExecutionStates, mode, setNodes]);

  return {
    syncActive: mode !== 'design',
    nodesTracked: nodeExecutionStates.size,
  };
};
